// Rendu partagé des systèmes d'évaluation (Le Passeport OU personnalisé) — Phase 3.
// Les valeurs numériques internes ne sont JAMAIS affichées : seules les positions
// servent à placer les points, l'axe affiche les libellés choisis par l'enseignant.
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { COLOR_META } from "./colors";
import { renderLevel } from "@/components/SystemCell";

// Palette de courbes (jusqu'à 10 critères).
export const LINE_PALETTE = [
  "#0284c7", "#7c3aed", "#059669", "#d97706", "#dc2626",
  "#0891b2", "#c026d3", "#65a30d", "#e11d48", "#4f46e5",
];

// Pastille / badge lecture seule d'un niveau, selon le type de système.
export function LevelDisplay({ system, levelId, size = "sm" }) {
  const dash = (
    <span
      className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-slate-300 bg-white text-[11px] text-slate-400 dark:border-white/15 dark:bg-transparent"
      title="Non évalué"
      data-testid="level-none"
    >—</span>
  );
  if (!system || system.type === "passeport") {
    if (!levelId || levelId === "white") return dash;   // « Non évalué » explicite
    const meta = COLOR_META[levelId] || COLOR_META.white;
    return (
      <span
        className={`inline-block h-3.5 w-3.5 shrink-0 rounded-full ${meta.dot}`}
        title={meta.meaning}
        data-level={levelId}
      />
    );
  }
  const level = (system.levels || []).find((l) => l.id === levelId);
  if (!level || level.is_none) return dash;
  return <span data-level={levelId}>{renderLevel(level, system.scale_type, size)}</span>;
}

// Signification lisible d'un niveau (pour les listes détaillées).
export function levelMeaning(system, levelId) {
  if (!system || system.type === "passeport") {
    return (COLOR_META[levelId] || COLOR_META.white).meaning;
  }
  const level = (system.levels || []).find((l) => l.id === levelId);
  return level ? level.label : "—";
}

// Graphique générique d'évolution (parent / fiche élève / tableau de bord).
export function EvalChart({ data, criteria, scale, height = 224 }) {
  const ticks = (scale?.ticks || []).map((t) => t.value);
  const labelByValue = {};
  (scale?.ticks || []).forEach((t) => { labelByValue[t.value] = t.label; });
  const min = scale?.min ?? 1;
  const max = scale?.max ?? 5;
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: -6, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} />
          <YAxis
            domain={[min, max]}
            ticks={ticks.length ? ticks : undefined}
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickFormatter={(v) => labelByValue[v] ?? v}
            width={96}
          />
          <Tooltip
            formatter={(value, name) => {
              const c = criteria.find((x) => x.id === name);
              return [labelByValue[value] ?? value, c ? c.label : name];
            }}
          />
          <Legend formatter={(value) => criteria.find((c) => c.id === value)?.label || value} />
          {criteria.map((c, i) => (
            <Line
              key={c.id} type="monotone" dataKey={c.id} name={c.id}
              stroke={LINE_PALETTE[i % LINE_PALETTE.length]} strokeWidth={2.5}
              dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Légende lecture seule d'un système (puce + libellé), pour les écrans famille.
export function EvalLegend({ system, className = "" }) {
  if (!system) return null;
  const levels = system.levels || [];
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 ${className}`} data-testid="eval-legend">
      {levels.map((l) => (
        <div key={l.id} className="flex items-center gap-1.5" title={l.label} data-testid={`legend-item-${l.id}`}>
          <LevelDisplay system={system} levelId={l.id} size="sm" />
          <span className="text-xs text-slate-500 dark:text-slate-400">{l.label}</span>
        </div>
      ))}
    </div>
  );
}
